/**
 * Experience Audit — reference view of shipped role-contextual defaults.
 * Route: /prioritization/settings/experience-audit
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ScanEye,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Navigation,
  ExternalLink,
  Search,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  buildExperienceAuditCatalog,
  PROFILE_SOURCE_LABELS,
  type ExperienceAuditEntry,
  type FamilyBaselineEntry,
} from '../../../lib/experienceAuditCatalog';
import type { ContributorNavSection, ContributorMyWorkItem } from '../../../lib/contributorNavProfiles';

const NAV_SECTION_LABELS: Record<ContributorNavSection, string> = {
  myWork: 'My Work',
  outputs: 'Outputs',
  workspace: 'Workspace',
};

const MY_WORK_LABELS: Record<ContributorMyWorkItem, string> = {
  sessions: 'Sessions',
  tasks: 'Tasks',
  exchange: 'Cascade',
};

function ProfileSourceBadge({ source }: { source: ExperienceAuditEntry['profileSource'] }) {
  const styles: Record<ExperienceAuditEntry['profileSource'], string> = {
    'role-override': 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30',
    'family-baseline': 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30',
    'workspace-member-baseline': 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)]',
    'admin-shell': 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)]',
  };
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${styles[source]}`}>
      {PROFILE_SOURCE_LABELS[source]}
    </span>
  );
}


function NavProfileDetails({ entry }: { entry: ExperienceAuditEntry }) {
  if (!entry.nav) {
    return (
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        Full configuration sidebar — not the contributor MY WORK · OUTPUTS · WORKSPACE shell.
      </p>
    );
  }

  const { nav } = entry;
  return (
    <dl className="grid gap-2 text-xs">
      <div className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1">
        <dt className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Shell</dt>
        <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)] capitalize">{nav.shell}</dd>
        <dt className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Primary link</dt>
        <dd>
          <Link to={nav.defaultPath} className="text-primary-600 hover:underline dark:text-[#5A9B72] inline-flex items-center gap-1">
            {nav.defaultPath}
            <ExternalLink className="h-3 w-3" />
          </Link>
        </dd>
        <dt className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Nav sections</dt>
        <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {nav.sectionOrder.map((s) => NAV_SECTION_LABELS[s]).join(' → ')}
        </dd>
        <dt className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">My Work order</dt>
        <dd className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {nav.myWorkOrder.map((item) => MY_WORK_LABELS[item]).join(' → ')}
        </dd>
      </div>
      {nav.roleNavItems && nav.roleNavItems.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">Role nav links</p>
          <ul className="space-y-0.5">
            {nav.roleNavItems.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="text-primary-600 hover:underline dark:text-[#5A9B72]">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </dl>
  );
}


/**
 * The home stopped varying by role on 2026-10-13 (DEP-030): the page of widgets
 * these defaults composed was retired, and every member's home answers the same
 * questions about what needs them. Only the navigation below still differs.
 */
const HOME_IS_THE_SAME =
  'The same for every role since 2026-10-13: the home answers what needs the person, not a role\'s widget layout (DEP-030). The role still sets the navigation.';

function AuditEntryCard({ entry, defaultOpen = false }: { entry: ExperienceAuditEntry; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:border-[var(--rule)] dark:bg-[var(--surface)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-[var(--surface)]/80 dark:hover:bg-[var(--bg-raised)] transition-colors"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-[var(--text-ter)] mt-0.5 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{entry.title}</h3>
            <ProfileSourceBadge source={entry.profileSource} />
            {entry.familyLabel && (
              <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">{entry.familyLabel}</span>
            )}
          </div>
          {entry.description && (
            <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] line-clamp-2">{entry.description}</p>
          )}
          {!open && (
            <p className="mt-2 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              {entry.nav ? `${entry.nav.shell} shell` : 'admin sidebar'}
            </p>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="grid gap-6 lg:grid-cols-2 pt-4">
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <Navigation className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  Navigation default
                </h4>
              </div>
              <NavProfileDetails entry={entry} />
            </div>

            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <LayoutDashboard className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  Home
                </h4>
              </div>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{HOME_IS_THE_SAME}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FamilyBaselineCard({ baseline }: { baseline: FamilyBaselineEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)]/50 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-3 text-left"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{baseline.label}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-[var(--rule)]/80 dark:border-[var(--rule)]">
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-2 mb-2">{baseline.description}</p>
        </div>
      )}
    </div>
  );
}

export default function ExperienceAudit() {
  const catalog = useMemo(() => buildExperienceAuditCatalog(), []);
  const [query, setQuery] = useState('');

  const filteredJobRoles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog.jobRoles;
    return catalog.jobRoles.filter(
      (entry) =>
        entry.title.toLowerCase().includes(q) ||
        entry.jobSlug?.includes(q) ||
        entry.familyLabel?.toLowerCase().includes(q) ||
        entry.description?.toLowerCase().includes(q),
    );
  }, [catalog.jobRoles, query]);

  const groupedJobRoles = useMemo(() => {
    const groups = new Map<string, ExperienceAuditEntry[]>();
    for (const entry of filteredJobRoles) {
      const key = entry.familyLabel ?? 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(entry);
    }
    return [...groups.entries()];
  }, [filteredJobRoles]);

  return (
    <div className="space-y-8 pp-settings">
      <PageHeader
        icon={<ScanEye className="h-4 w-4 text-[var(--text-sec)] dark:text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Experience Audit"
        subtitle="Reference view of every shipped role-contextual default: the navigation each role starts with. The home is the same for every role since 2026-10-13. Permissions still gate what each person can actually do."
      />

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] dark:bg-[var(--slate)]/20 dark:border-[var(--slate)]/60">
        <p className="text-sm text-[var(--text-sec)]">
          This page documents <strong>should-see</strong> defaults (Build Contract §1D). It does not impersonate users or
          change live workspace data. Multi-role users get a union of profiles; featured job roles pin one emphasis on the dashboard.
        </p>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">System defaults</h2>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Baselines when no job role applies, or when the admin configuration shell is active.
        </p>
        <div className="space-y-2">
          {catalog.systemDefaults.map((entry) => (
            <AuditEntryCard key={entry.id} entry={entry} defaultOpen />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Job family baselines</h2>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Shared dashboard templates used when a job role has no role-specific override.
        </p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {catalog.familyBaselines.map((baseline) => (
            <FamilyBaselineCard key={baseline.familyId} baseline={baseline} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Job roles</h2>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              {catalog.jobRoles.length} shipped roles — expand any card for navigation and dashboard defaults.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter roles…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
            />
          </div>
        </div>

        {filteredJobRoles.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] py-6 text-center">No roles match your search.</p>
        ) : (
          <div className="space-y-6">
            {groupedJobRoles.map(([family, entries]) => (
              <div key={family} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {family}
                </h3>
                <div className="space-y-2">
                  {entries.map((entry) => (
                    <AuditEntryCard key={entry.id} entry={entry} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
